
import React from "react";
import * as Hooks from "../../../hooks";
import { i18nManager } from "../../../i18n";
import { Schemes, Utils } from "../../../utils";
import { Elements } from "../../shared/Elements";
import MediaInput from "../../shared/MediaInput";
import * as ReactRouterDom from "react-router-dom";
import { ConfirmDialog, Siren } from "../../shared/ConfirmDialog";
import LoadingGif from "../../../assets/images/loading_circle.gif";
import { BoolHelper, Button, ButtonManageRef, DateInput, Dropdown, FormControl, ObjectHelper, Scheme, TextInput, useScreenDimension, useSubscription } from "@rivtn/noseur";
import { ApiResponseData, BaseService, CountryService, ICountry, IMember, IHost, SanitizedResponse, HostRole, HostService, ConsusService, IUploadedFile, OmneHostStatus } from "../../../services";

export interface ManageHostsProps {
    profile: IMember;
}

function ManageHosts(props: ManageHostsProps) {

    const { profile } = props;
    const { width } = useScreenDimension();
    const labels = i18nManager.Labels.host;
    useSubscription("HOST.FETCH", fetchHosts);
    const [themeMode, _] = Hooks.useThemeMode();
    const consusService = ConsusService.getInstance();
    const countryService = CountryService.getInstance();
    const tournamentHostService = HostService.getInstance();
    const [countries, setCountries] = React.useState<ICountry[]>();
    const [tournamentHosts, setHosts] = React.useState<ApiResponseData<IHost>>({});
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [status, setStatus] = React.useState<"ACTIVE" | "RETIRED">((searchParams.get("status") as any) ?? "ACTIVE");

    React.useEffect(() => {
        countryService.queryCountries({ name: "" }).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
            setCountries(apiResponse.data!.content);
        }).catch(BaseService.reportError);
    }, []);

    React.useEffect(() => {
        fetchHosts();
    }, [status]);

    return (<div className="teams">
        <div className="header">
            <span className="t">{labels.title}{status === "RETIRED" ? (<> - <span>{labels.retired_hosts}</span></>) : null}</span>
            <div className="controls">
                <Button raised scheme={Schemes.OMNEHOST} text={labels.create_host} onClick={createHost} />
            </div>
        </div>
        <div className="pagination">
            {Elements.buildPaginator(tournamentHosts ?? {},
                ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchHosts),
                parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
            {Elements.buildSorter(labels.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchHosts), labels.sorts[0].label)}
        </div>
        <div className="query-section">
            <div className="query-inputs">
                {labels.query_inputs.filter((f) => (width > 800 || BoolHelper.equalsAny(f.name, ["name"]))).map((fontControl) => (<TextInput key={fontControl.name}
                    placeholder={fontControl.placeholder} defaultValue={searchParams.get(fontControl.name) ?? undefined}
                    onInputComplete={(v) => Utils.updateSearchParams(fontControl.name, v, setSearchParams, fetchHosts)} />))}
            </div>
            <span className="link" style={{ marginLeft: 10 }} onClick={() => {
                setHosts({});
                const newStatus = (status === "ACTIVE" ? "RETIRED" : "ACTIVE");
                Utils.updateSearchParams("status", newStatus, setSearchParams, () => setStatus(newStatus));
            }}>{status === "ACTIVE" ? labels.view_retired_hosts : labels.view_active_hosts}</span>
        </div>
        <div className="teams-list hosts-list">
            {!tournamentHosts.content && (<div style={{ margin: "auto", display: "flex" }}>
                <img alt={"loading tournament hosts"} src={LoadingGif} style={{ width: 50, margin: "auto" }} />
            </div>)}
            {tournamentHosts.content && !tournamentHosts.content?.length && (Elements.buildEmptyListView("fa fa-fire",
                (status === OmneHostStatus.RETIRED ? (
                    <span>{labels.empty_label_parts.no_retired_hosts}</span>
                )
                    : (<>
                        <span>{labels.empty_label_parts.you_do_not} <span className="link" onClick={() => {
                            createHost();
                        }}>{labels.empty_label_parts.create}</span> {labels.empty_label_parts.or_update_search}</span>
                    </>))))}
            {tournamentHosts.content && !tournamentHosts.content?.length || true && (tournamentHosts.content?.map((tournamentHost) => {
                const hostWebsiteLink = Utils.buildPublicUrl("HOST", tournamentHost.public_id);
                if (tournamentHost?.un_reads?.notifications && tournamentHost?.un_reads?.notifications > 9) (tournamentHost!.un_reads as any).notifications = "9+";

                return (<div className="host" key={tournamentHost.external_id} style={{ borderColor: `rgba(${tournamentHost.extra_data?.color_scheme ?? "13, 217, 37"}, 0.2)` }}>
                    <div className='bg' style={{ backgroundImage: `url(${tournamentHost.extra_data?.primary_banner_image})` }} />
                    <Button rounded raised borderless linkTarget="_blank" link={hostWebsiteLink}
                        scheme={Schemes.OMNEHOST} className="visit" leftIcon="fa fa-computer" />
                    <div className='header'>
                        <img alt={tournamentHost.name} src={tournamentHost.logo_url} />
                        <span className='text-shadowed'>{tournamentHost.name}</span>
                    </div>
                    <div className='controls'>
                        {(status == OmneHostStatus.ACTIVE) && (<>
                            {tournamentHost.role == HostRole.OWNER && (<Button fill scheme={Schemes.OMNEHOST} className='visit' text={labels.controls.change_settings} link={`/hosts/${tournamentHost.public_id}/settings`} />)}
                            <Button fill scheme={Schemes.OMNEHOST} className='visit' link={`/hosts/${tournamentHost.public_id}/analytics`} text={labels.controls.analytics} />
                            {tournamentHost.role == HostRole.OWNER && <Button fill scheme={Schemes.OMNEHOST} className='visit' link={`/hosts/${tournamentHost.public_id}/create-tournament`} text={labels.controls.create_new_tournament} />}
                            {tournamentHost.role == HostRole.OWNER && <Button fill scheme={Schemes.OMNEHOST} className='visit' link={`/hosts/${tournamentHost.public_id}/settings?tab=MOBILE_APP`} text={labels.controls.generate_mobile_app} />}
                            <Button fill scheme={Schemes.OMNEHOST} className='visit' link={`/hosts/${tournamentHost.public_id}/settings?tab=MANAGE_TOURNAMENTS`} text={labels.controls.manage_tournaments} />
                            <Button fill scheme={Schemes.OMNEHOST} className='visit' text={labels.controls.chatroom} />
                            <Button fill scheme={Schemes.OMNEHOST} className='visit' text={labels.controls.notifications} link={`/hosts/${tournamentHost.public_id}/notifications?name=${tournamentHost.name}`} rightIcon={tournamentHost.un_reads?.notifications
                                ? (<span className="noseur-badge noseur-danger noseur-no-border" style={{ bottom: 3, right: 10 }}>{tournamentHost.un_reads?.notifications}</span>) : undefined} />
                            {tournamentHost.role == HostRole.MANAGER && (<Button fill textOnly scheme={Scheme.DANGER} className='visit'
                                text={labels.controls.leave_hosting} onClick={() => leaveHosting(tournamentHost)} />)}
                            {tournamentHost.role == HostRole.OWNER && (<Button fill textOnly scheme={Scheme.DANGER} className='visit'
                                text={labels.controls.retire_hosting} onClick={() => retireHosting(tournamentHost)} />)}
                        </>)}
                        {(tournamentHost.role == HostRole.OWNER && status == OmneHostStatus.RETIRED) && (<Button fill textOnly scheme={Scheme.SUCCESS} className='visit'
                            text={labels.controls.reactivate_hosting} onClick={() => reactivateHosting(tournamentHost)} />)}
                    </div>
                </div>);
            }))}
        </div>
    </div>);

    function fetchHosts() {
        setHosts({});
        tournamentHostService.queryHosts({
            status,
            sort: searchParams.get("sort") ?? "",
            name: searchParams.get("name") ?? "",
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "10",
            description: searchParams.get("description") ?? "",
            tournament_name: searchParams.get("tournament_name") ?? "",
        }).then(({ apiResponse }: SanitizedResponse<IHost>) => {
            setHosts(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function createHost() {
        const tournamentHost: Partial<IHost> = {};
        const dialogLabels = labels.dialog.create;
        const dialogErrorRef = { current: null } as any;
        const mediaInputManageRef = { current: undefined as any };
        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.create_host,
            style: { minWidth: (width < 800 ? "95%" : 500), maxWidth: "90%" },
            content: (<div style={{ margin: "10px 10px", marginBottom: 30 }}>
                <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end" }}>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", marginBottom: 10 }}>
                        <FormControl scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} isFieldset fill label={dialogLabels.name}>
                            <TextInput onInputComplete={(v) => {
                                tournamentHost.name = v;
                                dialogErrorRef.current!.innerText = " ";
                            }} />
                        </FormControl>
                        <FormControl isFieldset fill scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} label={dialogLabels.host_type.title}>
                            <Dropdown fill options={dialogLabels.host_type.options} onSelectOption={(o) => {
                                tournamentHost.entity_type = o.value;
                            }} />
                        </FormControl>
                    </div>
                    <MediaInput manageRef={mediaInputManageRef} style={{ alignSelf: "center", marginLeft: 20 }} width={180} height={180}
                        noFillTemplate onSelectFiles={(_) => dialogErrorRef.current!.innerText = " "} onGenerateWithAi={async () => {
                            return new File(["We are here"], "we.txt", { type: 'text/plain' });
                        }} />
                </div>
                <FormControl scheme={Schemes.OMNEHOST} style={{ marginTop: 10 }} isFieldset fill label={dialogLabels.country_of_operation.title}>
                    <Dropdown fill options={countries} optionMap={{ label: "name", value: "code" }} onSelectOption={(country: ICountry) => {
                        tournamentHost.country_code = country.code;
                    }} />
                </FormControl>
                <FormControl scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} isFieldset fill label={dialogLabels.means_of_id.title}>
                    <Dropdown fill onSelectOption={(o) => {

                    }} />
                </FormControl>
                <FormControl scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} isFieldset fill label={dialogLabels.id_number}>
                    <TextInput onInputComplete={(v) => {
                        //tournamentHost.name = v;
                        dialogErrorRef.current!.innerText = " ";
                    }} />
                </FormControl>
                <div style={{ display: "flex", flexDirection: "row" }}>
                    <FormControl isFieldset fill scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} label={dialogLabels.issue_date} >
                        <DateInput fill rightContent={"fa fa-calendar"} />
                    </FormControl>
                    <FormControl isFieldset fill scheme={Schemes.OMNEHOST} style={{ marginTop: 15, marginLeft: 15 }} label={dialogLabels.expiry_date} >
                        <DateInput fill rightContent={"fa fa-calendar"} />
                    </FormControl>
                </div>
                <MediaInput manageRef={mediaInputManageRef} style={{ alignSelf: "center", marginTop: 20, }} width="100%" height={180} instruction={dialogLabels.upload_proof_of_id}
                    noControl onSelectFiles={(_) => dialogErrorRef.current!.innerText = " "} />
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                const files = mediaInputManageRef.current?.files() as File[];
                button?.setLoadingState(true);
                const reportSuccess = () => {
                    button?.setLoadingState(false);
                    Siren.alert(dialogLabels.host_created_successfully);
                    dialog.hide();
                };
                tournamentHostService.createHost(tournamentHost).then(({ sanitized: sanitizedTeam, }: SanitizedResponse<IHost>) => {
                    const file = files[0];
                    consusService.uploadFile(`/omnehost/host/media/logo/`, `${sanitizedTeam.external_id}${ObjectHelper.getExtension(file.name, { lastOnly: true, })}`, file).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        tournamentHostService.updateHost(sanitizedTeam.external_id, { ...sanitizedTeam, logo_url: sanitized.consus_download_url }).then(() => {
                            fetchHosts();
                        }).catch(() => { }).finally(reportSuccess);
                    }).catch((error: any) => {
                        BaseService.reportError(error);
                        reportSuccess();
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function leaveHosting(tournamentHost: IHost) {
        let tournamentHostName = "";
        const dialogLabels = labels.dialog.leave;
        const dialogErrorRef = { current: null } as any;
        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${host_name}", tournamentHost.name),
            content: (<div style={{ margin: "10px 0px" }}>
                <FormControl style={{ marginTop: 15 }} isFieldset fill label={labels.dialog.confirm_host.host_name}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentHostName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentHostName !== tournamentHost.name) {
                    dialogErrorRef.current!.innerText = labels.dialog.confirm_host.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentHostService.leaveOrRetireHost(tournamentHost.external_id).then(({ apiResponse }: SanitizedResponse<IHost>) => {
                    dialog.destroy(() => {
                        fetchHosts();
                        Siren.alert(apiResponse.message);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function retireHosting(tournamentHost: IHost) {
        let tournamentHostName = "";
        const dialogLabels = labels.dialog.retire;
        const dialogErrorRef = { current: null } as any;
        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${host_name}", tournamentHost.name),
            content: (<div style={{ margin: "10px 0px" }}>
                <p>{labels.dialog.confirm_host.to_continue}</p>
                <FormControl style={{ marginTop: 15 }} isFieldset fill label={labels.dialog.confirm_host.host_name}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentHostName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentHostName !== tournamentHost.name) {
                    dialogErrorRef.current!.innerText = labels.dialog.confirm_host.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentHostService.leaveOrRetireHost(tournamentHost.external_id).then(({ apiResponse }: SanitizedResponse<IHost>) => {
                    dialog.destroy(() => {
                        fetchHosts();
                        Siren.alert(apiResponse.message);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function reactivateHosting(tournamentHost: IHost) {
        let tournamentHostName = "";
        const dialogLabels = labels.dialog.reactivate;
        const dialogErrorRef = { current: null } as any;
        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${host_name}", tournamentHost.name),
            content: (<div style={{ margin: "10px 0px" }}>
                <p>{labels.dialog.confirm_host.to_continue}</p>
                <FormControl style={{ marginTop: 15 }} isFieldset fill label={labels.dialog.confirm_host.host_name}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentHostName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentHostName !== tournamentHost.name) {
                    dialogErrorRef.current!.innerText = labels.dialog.confirm_host.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentHostService.reactivateHost(tournamentHost.external_id).then(({ apiResponse }: SanitizedResponse<IHost>) => {
                    dialog.destroy(() => {
                        fetchHosts();
                        Siren.alert(apiResponse.message);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default ManageHosts;
